import React, { useState, useEffect, useMemo } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import './style.scss';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import PerformanceUtils from 'services/PerformanceUtils';
import { useAppSelector } from 'careand-redux/store';

const PaymentPlanSetup = () => {
  let [loading, setLoading] = useState(false);
  let [plans, setPlans] = useState([]);
  let [permission, setPermission] = useState('');

  const dispatch = useAppDispatch();

  useEffect(() => {
    setLoading(true);
    dispatch(api.Payments.getAllPaymentPlans()).then((result) => {
      setLoading(false);
      if (result) setPlans(result);
    });
  }, [dispatch]);

  function onChange(event, planId, permissionIndex, value) {
    let modified = PerformanceUtils.clone(plans).map((plan) => {
      if (plan.id === planId) {
        plan.permissions[permissionIndex].value = value;
      }
      return plan;
    });
    setPlans(modified);
  }

  async function removePermission() {
    setLoading(true);
    const result = await dispatch(api.Payments.removeDefaultPermission({ name: permission }), {
      errorTitle: 'Remove Permission',
    });
    setLoading(false);
    if (result) setPlans(result);
  }

  async function addPermission() {
    setLoading(true);
    const result = await dispatch(api.Payments.addDefaultPermission({ name: permission }), {
      errorTitle: 'Add Permission',
    });
    setLoading(false);
    if (result) setPlans(result);
  }

  async function savePaymentPlans() {
    let clinicPaymentPlans = PerformanceUtils.clone(plans);
    setLoading(true);

    clinicPaymentPlans = clinicPaymentPlans.map((plan) => {
      delete plan.payment_plan;
      return plan;
    });

    await dispatch(api.Payments.editPaymentPlans({ clinicPaymentPlans }), {
      errorTitle: 'Payment Plans',
    });
    setLoading(false);
  }

  const archived = useMemo(() => {
    return plans.filter((plan) => !plan.payment_plan.active);
  }, [plans]);

  const active = useMemo(() => {
    return plans.filter((plan) => !!plan.payment_plan.active);
  }, [plans]);

  return (
    <div className="payment-plan-setup full-width">
      <ProgressMessage show={plans.length === 0} />
      <table width="100%">
        <tbody>
          <span className="small-title">Active</span>
          {active.map(
            (plan, index) =>
              plan !== undefined && (
                <React.Fragment key={plan.id}>
                  <Plan plan={plan} onChange={onChange} />
                </React.Fragment>
              ),
          )}
          <span className="small-title">Archived</span>
          {archived.map(
            (plan, index) =>
              plan !== undefined && (
                <React.Fragment key={plan.id}>
                  <Plan plan={plan} onChange={onChange} />
                </React.Fragment>
              ),
          )}
        </tbody>
      </table>
      <DefaultButton name="Save Plans" disabled={loading} onClick={savePaymentPlans} />
      <div className="row" style={{ marginTop: '20px' }}>
        <DefaultTextField
          name="permission"
          placeholder="Permission Name"
          style={{ width: '250px' }}
          value={permission}
          onChange={(event) => setPermission(event.target.value)}
        />
        <DefaultButton
          name="Remove Permission"
          disabled={loading}
          className="red"
          style={{ marginLeft: '20px' }}
          onClick={removePermission}
        />
        <DefaultButton
          name="Add Permission"
          disabled={loading}
          style={{ marginLeft: '20px' }}
          onClick={addPermission}
        />
      </div>
    </div>
  );
};

const Plan = ({ plan, onChange }) => (
  <tr>
    <td className="name">
      {plan.payment_plan.name} - {plan.payment_plan.interval}
    </td>
    {plan.permissions.map((permission, arrayIndex) => (
      <Value
        key={arrayIndex}
        value={permission.value}
        onChange={(event) => onChange(event, plan.id, arrayIndex, event.target.value)}
        name={plan.id + '-' + permission.name}
        label={permission.name}
      />
    ))}
  </tr>
);

const Value = ({ value, onChange, name, label }) => (
  <td>
    <span className="label">{label}:</span>
    <DefaultTextField
      name={name}
      value={value || ''}
      onChange={onChange}
      style={{ width: '40px' }}
    />
  </td>
);

export default React.memo(PaymentPlanSetup);
